import React, { useState, useEffect } from 'react';
import { X, Upload, User, Bell, Save, Mail } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { toast } from 'sonner';
import { EmailSignatureEditor } from './EmailSignatureEditor';

import { bearerAuthHeader } from '../utils/apiHeaders';
interface ProfileSettingsProps {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    avatarUrl?: string;
  };
  companyId: string;
  projectId: string;
  publicAnonKey: string;
  accessToken?: string;
  onClose: () => void;
}

export function ProfileSettings({ user, companyId, projectId, publicAnonKey, accessToken, onClose }: ProfileSettingsProps) {
  const [avatarUrl, setAvatarUrl] = useState(user.avatarUrl || '');
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Notification preferences
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [inAppNotifications, setInAppNotifications] = useState(true);
  const [generalMessages, setGeneralMessages] = useState(true);
  const [ciMessages, setCiMessages] = useState(true);
  const [blockers, setBlockers] = useState(true);
  const [approvals, setApprovals] = useState(true);

  // Load notification preferences
  useEffect(() => {
    fetchNotificationPreferences();
  }, []);

  const fetchNotificationPreferences = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/user/${user.id}/notification-preferences`,
        {
          headers: {
            ...bearerAuthHeader(),
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.preferences) {
          setEmailNotifications(data.preferences.email !== false);
          setInAppNotifications(data.preferences.inApp !== false);
          setGeneralMessages(data.preferences.generalMessages !== false);
          setCiMessages(data.preferences.ciMessages !== false);
          setBlockers(data.preferences.blockers !== false);
          setApprovals(data.preferences.approvals !== false);
        }
      }
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setIsUploading(true);

    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', user.id);
      formData.append('companyId', companyId);

      // Upload to server
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/user/${user.id}/avatar`,
        {
          method: 'POST',
          headers: {
            ...bearerAuthHeader(),
          },
          body: formData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAvatarUrl(data.avatarUrl);
        toast.success('Avatar uploaded successfully');
      } else {
        // Get detailed error message from server
        const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
        const errorMessage = errorData.error || errorData.message || 'Upload failed';
        toast.error(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      // Only show error if we haven't already shown one from the response
      if (error instanceof Error && error.message && !error.message.includes('Upload failed')) {
        toast.error(error.message);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveNotificationPreferences = async () => {
    setIsSaving(true);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/user/${user.id}/notification-preferences`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...bearerAuthHeader(),
          },
          body: JSON.stringify({
            email: emailNotifications,
            inApp: inAppNotifications,
            generalMessages,
            ciMessages,
            blockers,
            approvals,
          }),
        }
      );

      if (response.ok) {
        toast.success('Notification preferences saved');
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Check if user is a consultant (CF Admin or Consultant role)
  const isConsultant = user.role === 'cfAdmin' || user.role === 'consultant' || user.role === 'cf_admin' || user.role === 'cf_consultant';

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Profile Settings</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="profile" className="mt-4">
          <TabsList className={isConsultant ? "grid w-full grid-cols-3" : "grid w-full grid-cols-2"}>
            <TabsTrigger value="profile">
              <User className="h-4 w-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="notifications">
              <Bell className="h-4 w-4 mr-2" />
              Notifications
            </TabsTrigger>
            {isConsultant && accessToken && (
              <TabsTrigger value="signature">
                <Mail className="h-4 w-4 mr-2" />
                Email Signature
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="profile" className="space-y-6 mt-6">
            {/* Avatar Upload */}
            <div className="flex flex-col items-center gap-4">
              <Avatar className="h-24 w-24">
                <AvatarImage src={avatarUrl} alt={user.name} />
                <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                  {getInitials(user.name)}
                </AvatarFallback>
              </Avatar>

              <div className="flex flex-col items-center gap-2">
                <Label htmlFor="avatar-upload" className="cursor-pointer">
                  <div className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors">
                    <Upload className="h-4 w-4" />
                    <span>{isUploading ? 'Uploading...' : 'Upload Avatar'}</span>
                  </div>
                </Label>
                <Input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarUpload}
                  disabled={isUploading}
                />
                <p className="text-xs text-muted-foreground text-center">
                  PNG, JPG up to 5MB
                </p>
              </div>
            </div>

            {/* User Info (Read-only) */}
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input value={user.name} disabled className="mt-1.5" />
              </div>

              <div>
                <Label>Email</Label>
                <Input value={user.email} disabled className="mt-1.5" />
              </div>

              <div>
                <Label>Role</Label>
                <Input
                  value={user.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  disabled
                  className="mt-1.5"
                />
              </div>

              <p className="text-sm text-muted-foreground">
                Contact your administrator to change your name, email, or role.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="notifications" className="space-y-6 mt-6">
            <div className="space-y-6">
              {/* Notification Channels - HOW */}
              <div>
                <h3 className="text-base font-semibold mb-2">Notification Channels</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Choose how you want to receive notifications
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="space-y-0.5">
                      <Label htmlFor="email-notifications" className="cursor-pointer">Email notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Receive email alerts for new messages
                      </p>
                    </div>
                    <Switch
                      id="email-notifications"
                      checked={emailNotifications}
                      onCheckedChange={setEmailNotifications}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="space-y-0.5">
                      <Label htmlFor="inapp-notifications" className="cursor-pointer">In-app notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Show notification badge and alerts in the app
                      </p>
                    </div>
                    <Switch
                      id="inapp-notifications"
                      checked={inAppNotifications}
                      onCheckedChange={setInAppNotifications}
                    />
                  </div>
                </div>
              </div>

              {/* Message Types - WHAT */}
              {(emailNotifications || inAppNotifications) && (
                <div className="border-t pt-6">
                  <h3 className="text-base font-semibold mb-2">Message Types</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Choose what types of messages you want to be notified about
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="space-y-0.5">
                        <Label htmlFor="general-messages" className="cursor-pointer">General Messages</Label>
                        <p className="text-sm text-muted-foreground">
                          Company-wide announcements and updates
                        </p>
                      </div>
                      <Switch
                        id="general-messages"
                        checked={generalMessages}
                        onCheckedChange={setGeneralMessages}
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="space-y-0.5">
                        <Label htmlFor="ci-messages" className="cursor-pointer">Config Item Messages</Label>
                        <p className="text-sm text-muted-foreground">
                          Messages about specific configuration items
                        </p>
                      </div>
                      <Switch
                        id="ci-messages"
                        checked={ciMessages}
                        onCheckedChange={setCiMessages}
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg bg-red-50/50">
                      <div className="space-y-0.5">
                        <Label htmlFor="blockers" className="cursor-pointer">Blockers</Label>
                        <p className="text-sm text-muted-foreground">
                          Urgent issues requiring immediate attention
                        </p>
                      </div>
                      <Switch
                        id="blockers"
                        checked={blockers}
                        onCheckedChange={setBlockers}
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg bg-blue-50/50">
                      <div className="space-y-0.5">
                        <Label htmlFor="approvals" className="cursor-pointer">Approvals</Label>
                        <p className="text-sm text-muted-foreground">
                          Requests requiring your approval
                        </p>
                      </div>
                      <Switch
                        id="approvals"
                        checked={approvals}
                        onCheckedChange={setApprovals}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Warning if all channels disabled */}
              {!emailNotifications && !inAppNotifications && (
                <div className="border-t pt-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      You have disabled all notification channels. You will not receive any alerts about new messages.
                    </p>
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <Button onClick={handleSaveNotificationPreferences} disabled={isSaving} className="w-full">
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save Preferences'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {isConsultant && accessToken && (
            <TabsContent value="signature" className="space-y-6 mt-6">
              <EmailSignatureEditor accessToken={accessToken} />
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}